import NavbarV2 from '../../components/navbarV2/NavbarV2';
import './createCreditNoteV2.css';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as React from 'react';
import OrdersList from '../../components/OrdersList/OrdersList';
import CartList from '../../components/CartList/CartList';
import { toast } from 'react-toastify';
import ListEmptyStateV2, { ListPageLoadingV2 } from '../../components/listEmptyStateV2/ListEmptyStateV2';
import { FaReceipt } from 'react-icons/fa';
import { formatMoney } from '../../utils/formatMoney';

const CreateCreditNoteV2 = () => {
    const [orders, setOrders] = useState([]);
    const [ordersLoaded, setOrdersLoaded] = useState(false);
    const [order, setOrder] = useState(undefined);
    const [selectedOrder, setSelectedOrder] = useState(undefined);
    const [creditTotal, setCreditTotal] = useState(0);
    const [activeStep, setActiveStep] = React.useState(0);
    const [surcharge, setSurcharge] = useState(0);
    const [shipping, setShipping] = useState(0);
    const [editingCreditNote, setEditingCreditNote] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate();
    const params = useParams();

    const steps = isEditing ? ['Configure credit note', 'Review'] : ['Select order', 'Configure credit note', 'Review'];

    useEffect(() => {
        if (localStorage.getItem('admin')) {
            getOrders();
            if (params.id) {
                const urlParams = new URLSearchParams(window.location.search);
                const creditNoteId = urlParams.get('creditNoteId');

                getOrder(params.id, creditNoteId);
                setSelectedOrder(params.id);
                if (creditNoteId) {
                    setIsEditing(true);
                    setActiveStep(0);
                } else {
                    setActiveStep(1);
                }
            }
        } else {
            navigate('/');
        }
    }, []);

    const getOrders = () => {
        const url = `${process.env.REACT_APP_API}/api/getAllOrders`;

        fetch(url, {})
            .then((response) => {
                if (response.status === 200) {
                    if (localStorage.getItem('account') !== null) {
                        response.json().then((data) => setOrders(data));
                    } else {
                        navigate('/');
                    }
                }
            })
            .finally(() => setOrdersLoaded(true));
    };

    const getOrder = (id, creditNoteId = null) => {
        fetch(`${process.env.REACT_APP_API}/api/getOrder/${id}`, {})
            .then((response) => {
                if (response.status === 200) {
                    if (localStorage.getItem('account') !== null) {
                        response.json().then((data) => {
                            if (creditNoteId && data.creditNotes) {
                                const creditNote = data.creditNotes.find((cn) => cn._id === creditNoteId);
                                if (creditNote) {
                                    setEditingCreditNote(creditNote);
                                    setSurcharge(creditNote.surcharge || 0);
                                    setShipping(creditNote.shipping || 0);

                                    const cartWithCreditAmounts = data.cart.map((cartItem) => {
                                        const creditItem = creditNote.items.find((item) => item.SKU === cartItem.id);
                                        return {
                                            ...cartItem,
                                            creditAmount: creditItem ? creditItem.amount : 0,
                                        };
                                    });
                                    setOrder({ ...data, cart: cartWithCreditAmounts });
                                } else {
                                    setOrder({
                                        ...data,
                                        cart: data.cart.map((x) => ({ ...x, creditAmount: 0 })),
                                    });
                                }
                            } else {
                                setOrder({
                                    ...data,
                                    cart: data.cart.map((x) => ({ ...x, creditAmount: 0 })),
                                });
                            }
                        });
                    } else {
                        navigate('/');
                    }
                }
            });
    };

    const select = (id) => {
        setSelectedOrder(id);
        getOrder(id);
        setCreditTotal(0);
    };

    const handleNext = () => {
        const last = steps.length - 1;
        if (activeStep === last) {
            create();
            return;
        }
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const updateCart = (cart) => {
        setOrder({ ...order, cart });
        calculateCreditTotal(cart);
    };

    const calculateCreditTotal = (cart) => {
        let total = 0;

        for (let i = 0; i < cart.length; i++) {
            const item = cart[i];

            if (item.creditAmount > 0) total += item.creditAmount * item.price;
        }

        setCreditTotal(total);
    };

    const create = () => {
        const tempItems = [];

        for (let i = 0; i < order.cart.length; i++) {
            const item = order.cart[i];

            if (item.creditAmount > 0)
                tempItems.push({
                    SKU: item.id,
                    name: item.name,
                    amount: item.creditAmount,
                    price: item.price,
                });
        }

        const requestBody = {
            creditNote: tempItems,
            surcharge,
            shipping,
        };

        if (isEditing && editingCreditNote) {
            requestBody.creditNoteId = editingCreditNote._id;
        }

        fetch(`${process.env.REACT_APP_API}/api/changeCreditNote/${order._id}`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify(requestBody),
        }).then((response) => {
            if (response.status === 200) {
                toast.success(isEditing ? 'Updated Credit Note!' : 'Created Credit Note!');
                navigate('/creditnotes');
            } else if (response.status === 500) {
                toast.error('Server error.');
            }
        });
    };

    const getCombinedTotal = () => {
        let total = 0;

        total += Number(creditTotal);
        total += Number(surcharge);
        total += Number(shipping);

        return total;
    };

    const lastStepIndex = steps.length - 1;
    const onSelectOrderStep = activeStep === 0 && !isEditing;
    const onConfigureStep =
        (activeStep === 1 && !isEditing) || (activeStep === 0 && isEditing);
    const onReviewStep =
        (activeStep === 2 && !isEditing) || (activeStep === 1 && isEditing);

    const nextDisabled =
        ((onSelectOrderStep || onConfigureStep) && order === undefined);

    return (
        <div className="v2-page createcreditnotev2">
            <NavbarV2 />

            <div className="v2-container createcreditnotev2__inner">
                <header className="createcreditnotev2__header">
                    <h1 className="v2-h1">{isEditing ? 'Edit Credit Note' : 'Create Credit Note'}</h1>
                    <p className="v2-muted createcreditnotev2__lede">
                        {isEditing
                            ? 'Adjust credit quantities and line totals, then review before updating.'
                            : 'Choose an order, set credit line amounts, then review before issuing the credit note.'}
                    </p>
                </header>

                <nav className="createcreditnotev2__stepper" aria-label="Progress">
                    <ol className="createcreditnotev2__steps">
                        {steps.map((label, i) => (
                            <li
                                key={label}
                                className={
                                    i < activeStep
                                        ? 'createcreditnotev2__step createcreditnotev2__step--done'
                                        : i === activeStep
                                          ? 'createcreditnotev2__step createcreditnotev2__step--current'
                                          : 'createcreditnotev2__step createcreditnotev2__step--todo'
                                }
                            >
                                <span className="createcreditnotev2__step-index" aria-hidden="true">
                                    {i + 1}
                                </span>
                                <span className="createcreditnotev2__step-label">{label}</span>
                            </li>
                        ))}
                    </ol>
                </nav>

                <section className="v2-card createcreditnotev2__panel v2-mui-scope">
                    {onSelectOrderStep && !ordersLoaded && <ListPageLoadingV2 />}

                    {onSelectOrderStep && ordersLoaded && orders.length === 0 && (
                        <ListEmptyStateV2
                            icon={FaReceipt}
                            title="No orders available"
                            description="There are no orders to attach a credit note to yet."
                        />
                    )}

                    {onSelectOrderStep && orders.length > 0 && (
                        <OrdersList
                            data={orders}
                            invoices={false}
                            selectMode={true}
                            select={select}
                            selectedOrder={selectedOrder}
                            embed={true}
                        />
                    )}

                    {onConfigureStep && !order && <ListPageLoadingV2 />}

                    {onConfigureStep && order && (
                        <div className="createcreditnotev2__configure">
                            <h2 className="v2-card-title">Line items &amp; adjustments</h2>
                            <CartList
                                data={order.cart}
                                showcase={true}
                                update={updateCart}
                                creditnote={true}
                                embed={true}
                            />
                            <div className="createcreditnotev2__totals">
                                <div className="createcreditnotev2__total-line">
                                    <span>Order total (credit)</span>
                                    <strong>- €{formatMoney(creditTotal)}</strong>
                                </div>
                                <label className="createcreditnotev2__inline-field">
                                    <span>Surcharge</span>
                                    <input
                                        type="number"
                                        value={surcharge}
                                        onChange={(e) => setSurcharge(e.target.value)}
                                    />
                                </label>
                                <label className="createcreditnotev2__inline-field">
                                    <span>Shipping</span>
                                    <input
                                        type="number"
                                        value={shipping}
                                        onChange={(e) => setShipping(e.target.value)}
                                    />
                                </label>
                                <div className="createcreditnotev2__total-line createcreditnotev2__total-line--muted">
                                    <span>VAT (18%)</span>
                                    <span>- €{formatMoney((getCombinedTotal() / 1.18) * 0.18)}</span>
                                </div>
                                <div className="createcreditnotev2__total-line createcreditnotev2__total-line--grand">
                                    <span>Combined total</span>
                                    <strong>- €{formatMoney(getCombinedTotal())}</strong>
                                </div>
                            </div>
                        </div>
                    )}

                    {onReviewStep && !order && <ListPageLoadingV2 />}

                    {onReviewStep && order && (
                        <div className="createcreditnotev2__review">
                            <h2 className="v2-card-title">Review credit note</h2>
                            <CartList
                                data={order.cart
                                    .filter((x) => x.creditAmount > 0)
                                    .map((x) => ({ ...x, amount: x.creditAmount }))}
                                showcase={true}
                                embed={true}
                            />
                            <div className="createcreditnotev2__totals">
                                <div className="createcreditnotev2__total-line">
                                    <span>Order total (credit)</span>
                                    <strong>- €{formatMoney(creditTotal)}</strong>
                                </div>
                                <div className="createcreditnotev2__total-line">
                                    <span>Surcharge</span>
                                    <strong>- €{formatMoney(surcharge || 0)}</strong>
                                </div>
                                <div className="createcreditnotev2__total-line">
                                    <span>Shipping</span>
                                    <strong>- €{formatMoney(shipping || 0)}</strong>
                                </div>
                                <div className="createcreditnotev2__total-line createcreditnotev2__total-line--muted">
                                    <span>VAT (18%)</span>
                                    <span>- €{formatMoney((getCombinedTotal() / 1.18) * 0.18)}</span>
                                </div>
                                <div className="createcreditnotev2__total-line createcreditnotev2__total-line--grand">
                                    <span>Combined total</span>
                                    <strong>- €{formatMoney(getCombinedTotal())}</strong>
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                <div className="createcreditnotev2__actions">
                    <button
                        type="button"
                        className="v2-btn v2-btn--outline"
                        onClick={handleBack}
                        disabled={activeStep === 0}
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        className="v2-btn v2-btn--secondary"
                        onClick={handleNext}
                        disabled={nextDisabled}
                    >
                        {activeStep === lastStepIndex ? (isEditing ? 'Update' : 'Finish') : 'Next'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateCreditNoteV2;
